import {
  BOGGLE_SPECIAL_TILE_KINDS,
  BOGGLE_SPECIAL_TILE_REGISTRY,
  DEFAULT_BOGGLE_PLAYGROUND_SPECIAL_TILE_SETTINGS,
  type BogglePlaygroundSpecialTileSettings,
  type BoggleSpecialTileKind,
  type BoggleSpecialTileSettings,
} from '~/boggle/engine/specialTiles'
import type { BogglePlaygroundSettings } from '~/boggle/config/playground'
import {
  getBoggleGridDimensions,
  normalizeBogglePlaygroundSettings,
} from '~/boggle/lib/playground'
import {
  getPositiveSelectionWeight,
  pickWeightedEntry,
} from '~/boggle/lib/challenge/random'
import {
  BOGGLE_CHALLENGE_SPECIAL_TILE_CADENCE_FIELDS,
  type BoggleChallengeSpecialTileChoice,
  type BoggleChallengeSpecialTileEligibility,
  type BoggleChallengeSpecialTileOptionValue,
  type BoggleChallengeSpecialTileRevealEntry,
  type BoggleChallengeSpecialTileRollResult,
  type BoggleChallengeSpecialTileTag,
} from '~/boggle/config/specialTiles/challengeSpecialTileTypes'

export * from '~/boggle/config/specialTiles/challengeSpecialTileTypes'

type BoggleChallengeSpecialTileModeFlags = Pick<
  BogglePlaygroundSettings,
  'endIntervalOnSubmit' | 'lettersRepopulate' | 'gridSize'
>

function createDisabledSpecialTilePatch(): BogglePlaygroundSpecialTileSettings {
  return Object.fromEntries(
    BOGGLE_SPECIAL_TILE_KINDS.map(kind => [
      kind,
      {
        ...DEFAULT_BOGGLE_PLAYGROUND_SPECIAL_TILE_SETTINGS[kind],
      },
    ]),
  ) as BogglePlaygroundSpecialTileSettings
}

function getBoardCellCount(settings: Pick<BogglePlaygroundSettings, 'gridSize'>) {
  const dimensions = getBoggleGridDimensions(settings.gridSize)
  return dimensions.cols * dimensions.rows
}

function resolveVariantStartingCount(
  choice: BoggleChallengeSpecialTileChoice,
  settings: Pick<BogglePlaygroundSettings, 'gridSize'>,
) {
  if (choice.startingCount === 'all') {
    return getBoardCellCount(settings)
  }

  return choice.startingCount
}

function resolveVariantMaximumCount(
  choice: BoggleChallengeSpecialTileChoice,
  settings: Pick<BogglePlaygroundSettings, 'gridSize'>,
) {
  if (choice.maximumCount === 'match-start') {
    return resolveVariantStartingCount(choice, settings)
  }

  return choice.maximumCount
}

function isChoiceCompatible(
  choice: BoggleChallengeSpecialTileChoice,
  modeFlags: BoggleChallengeSpecialTileModeFlags,
  selectedTags: ReadonlySet<BoggleChallengeSpecialTileTag>,
  blockedTags: ReadonlySet<BoggleChallengeSpecialTileTag>,
) {
  if (choice.excludeWhenEndIntervalOnSubmit && modeFlags.endIntervalOnSubmit) {
    return false
  }

  if (choice.excludeWhenLettersDoNotRepopulate && modeFlags.lettersRepopulate === false) {
    return false
  }

  if (choice.tags.some(tag => blockedTags.has(tag))) {
    return false
  }

  if (choice.excludesTags.some(tag => selectedTags.has(tag))) {
    return false
  }

  return true
}

function toSpecialTileChoiceArray(value: BoggleChallengeSpecialTileOptionValue) {
  return (
    Array.isArray(value) ? value : [value]
  ) as readonly BoggleChallengeSpecialTileChoice[]
}

function isSpecialTileSuiteCompatible(
  choices: readonly BoggleChallengeSpecialTileChoice[],
  modeFlags: BoggleChallengeSpecialTileModeFlags,
  blockedTags: ReadonlySet<BoggleChallengeSpecialTileTag> = new Set(),
) {
  const selectedTags = new Set<BoggleChallengeSpecialTileTag>()
  const selectedKinds = new Set<BoggleSpecialTileKind>()

  for (const choice of choices) {
    if (selectedKinds.has(choice.kind)) {
      return false
    }

    if (!isChoiceCompatible(choice, modeFlags, selectedTags, blockedTags)) {
      return false
    }

    selectedKinds.add(choice.kind)
    for (const tag of choice.tags) {
      selectedTags.add(tag)
    }
  }

  return true
}

export function buildRevealEntry(
  choice: BoggleChallengeSpecialTileChoice,
): BoggleChallengeSpecialTileRevealEntry {
  const tileDefinition = BOGGLE_SPECIAL_TILE_REGISTRY[choice.kind]

  return {
    behaviorId: choice.id,
    behaviorLabel: choice.label,
    behaviorTags: choice.tags,
    description: choice.description,
    interaction: choice.interactionPresentation,
    interactionId: choice.interactionId,
    kind: choice.kind,
    kindIntrinsicDescription: tileDefinition.description,
    supply: choice.supplyPresentation,
    supplyId: choice.supplyId,
    tileDescription: tileDefinition.description,
    tileLabel: tileDefinition.label,
  }
}

export function resolvePackageVariantSettings(
  choice: BoggleChallengeSpecialTileChoice,
  modeFlags: BoggleChallengeSpecialTileModeFlags,
) {
  const { returnRule, runtimeHooks } = choice
  const startingCount = resolveVariantStartingCount(choice, modeFlags)
  const maximumCount = resolveVariantMaximumCount(choice, modeFlags)

  return {
    ...DEFAULT_BOGGLE_PLAYGROUND_SPECIAL_TILE_SETTINGS[choice.kind],
    clearOnUse: runtimeHooks?.clearOnUse ?? false,
    clearOnDisuse: runtimeHooks?.clearOnDisuse ?? false,
    countAtStart: startingCount,
    convertXTilesOnSubmit: null,
    enabled: true,
    maximumCount,
    persistDuringBoardClears: runtimeHooks?.persistDuringBoardClears ?? false,
    replaceWhenCleared: returnRule.type === 'replace-when-cleared',
    resetWhenEmpty: returnRule.type === 'reset-when-empty',
    spawnChancePercent: null,
    spawnEveryXTiles: returnRule.type === 'spawn-cadence' ? returnRule.everyXTiles : null,
    spawnXTilesOnSubmit: null,
    spreadToAllAdjacentOnUse: runtimeHooks?.spreadToAllAdjacentOnUse ?? false,
    spreadToOneAdjacentOnDisuse: runtimeHooks?.spreadToOneAdjacentOnDisuse ?? false,
  } satisfies Partial<BoggleSpecialTileSettings>
}

export function getBoggleChallengeSpecialTileCadences(
  settings: Pick<BogglePlaygroundSettings, 'specialTiles'>,
) {
  return BOGGLE_SPECIAL_TILE_KINDS.flatMap(kind => {
    const specialTile = settings.specialTiles[kind]
    if (!specialTile.enabled) {
      return []
    }

    return BOGGLE_CHALLENGE_SPECIAL_TILE_CADENCE_FIELDS.flatMap(field => {
      const value = specialTile[field]
      const active =
        field === 'replaceWhenCleared' || field === 'resetWhenEmpty'
          ? value === true
          : value !== null && value !== undefined

      return active
        ? [
            {
              field,
              kind,
            },
          ]
        : []
    })
  })
}

export function rollBoggleChallengeSpecialTiles(
  settings: BogglePlaygroundSettings,
  rngState: number,
  eligibility: BoggleChallengeSpecialTileEligibility,
): BoggleChallengeSpecialTileRollResult {
  const modeFlags = {
    endIntervalOnSubmit: settings.endIntervalOnSubmit,
    gridSize: settings.gridSize,
    lettersRepopulate: settings.lettersRepopulate,
  }
  const specialTiles = createDisabledSpecialTilePatch()
  const compatibleOptions = eligibility.options.filter(option =>
    isSpecialTileSuiteCompatible(toSpecialTileChoiceArray(option.value), modeFlags),
  )

  if (compatibleOptions.length === 0) {
    throw new Error('No compatible special tile options available for this challenge')
  }

  const pickedOption =
    compatibleOptions.length === 1 &&
    getPositiveSelectionWeight(compatibleOptions[0]!.weight) > 0
      ? {
          entry: compatibleOptions[0]!,
          rngState,
        }
      : pickWeightedEntry(compatibleOptions, rngState)
  if (pickedOption === null) {
    throw new Error('No weighted special tile options available for this challenge')
  }
  const choices = toSpecialTileChoiceArray(pickedOption.entry.value)

  for (const choice of choices) {
    specialTiles[choice.kind] = resolvePackageVariantSettings(choice, modeFlags)
  }

  const revealEntries = choices.map(buildRevealEntry)

  const settingsPatch = {
    specialTiles,
  }

  return {
    revealEntries,
    rngState: pickedOption.rngState,
    settings: normalizeBogglePlaygroundSettings(settingsPatch, settings),
    settingsPatch,
  }
}
